// --- Enums ---

export type TaskStatus = 'draft' | 'active' | 'paused' | 'archived';
export type TaskCategory = 'experience' | 'content' | 'survey' | 'onboarding' | 'activity' | 'verification';
export type TaskType = 'normal' | 'other_normal' | 'download';
export type MessageTaskType = 'message_send';
export type AnyTaskType = TaskType | MessageTaskType;
export type TaskPlatform = 'ios' | 'android' | 'web' | 'all';

// --- Labels ---

export const TASK_STATUS_LABELS: Record<TaskStatus, string> = {
  draft: '草稿',
  active: '进行中',
  paused: '暂停',
  archived: '归档',
};

export const TASK_CATEGORY_LABELS: Record<TaskCategory, string> = {
  experience: '体验',
  content: '内容',
  survey: '问卷',
  onboarding: '引导',
  activity: '活动',
  verification: '认证',
};

export const TASK_TYPE_LABELS: Record<AnyTaskType, string> = {
  normal: '普通任务',
  other_normal: '其他普通任务',
  download: '下载任务',
  message_send: '短信代发',
};

export const TASK_PLATFORM_LABELS: Record<TaskPlatform, string> = {
  ios: 'iOS',
  android: 'Android',
  web: 'Web',
  all: '全平台',
};

export const ALL_TASK_STATUSES: TaskStatus[] = ['draft', 'active', 'paused', 'archived'];
export const ALL_TASK_CATEGORIES: TaskCategory[] = ['experience', 'content', 'survey', 'onboarding', 'activity', 'verification'];
export const ALL_TASK_TYPES: AnyTaskType[] = ['normal', 'other_normal', 'download', 'message_send'];
export const ALL_TASK_PLATFORMS: TaskPlatform[] = ['ios', 'android', 'web', 'all'];

export const TASK_STATUS_TRANSITIONS: Record<TaskStatus, TaskStatus[]> = {
  draft: ['active', 'archived'],
  active: ['paused', 'archived'],
  paused: ['active', 'archived'],
  archived: ['paused', 'active'],
};

export const TASK_STATUS_ACTION_LABELS: Record<TaskStatus, string> = {
  draft: '退回草稿',
  active: '发布',
  paused: '暂停',
  archived: '归档',
};

// --- Sub-structures ---

export interface TaskStepLinkDTO {
  url: string;
  label: string;
}

export interface TaskStepDTO {
 videos?: { id: string; url: string; poster_url: string; duration: number; width: number; height: number }[];
  id: string;
  order: number;
  title: string;
  description: string;
  images: string[];
  links: TaskStepLinkDTO[];
}

export interface DownloadAppInfoDTO {
  id: number;
  bundle_id: string;
  search_keywords: string[];
  search_keyword_quotas?: DownloadKeywordQuotaDTO[];
  name: string;
  version: string;
  price: number;
  formatted_price: string;
  artwork_url: string;
  developer_name: string;
  description: string;
  genre: string;
  file_size_bytes: number;
  rating: number;
  rating_count: number;
  content_rating: string;
  screenshot_urls: string[];
}

export interface DownloadKeywordQuotaDTO {
  keyword: string;
  target_count: number;
  completed_count: number;
}

export interface MessageImportRowDTO {
  row_no: number;
  status: string;
  error_code?: string;
  raw_recipient_type?: string;
  raw_recipient?: string;
  raw_variable?: string;
  raw_body?: string;
}

export interface MessageImportBatchDTO {
  batch_id: string;
  task_id?: string;
  source_sha256?: string;
  status: string;
  total_rows: number;
  valid_rows: number;
  invalid_rows: number;
  duplicate_rows: number;
  suppressed_rows: number;
  committed_rows: number;
  expires_at?: string | null;
  rows?: MessageImportRowDTO[];
}

export interface MessagePoolStatsDTO {
  available: number;
  assigned: number;
  sent: number;
  reviewed: number;
  suppressed: number;
  total?: number;
  kill_switch_enabled?: boolean;
  kill_switch_configured?: boolean;
  recent_batches?: MessageImportBatchDTO[];
}

// --- Task DTO ---

export interface TaskDTO {
  source?: string;
  agent_id?: number;
  agent_name?: string;
  agent_portal_task_id?: number;
  agent_portal_revision_id?: number;
  claim_snapshot?: { revision_id?: number; source?: string; agent_id?: number; agent_portal_task_id?: number; reward_amount_minor?: number; };
  attempt_no?: number;

  id: number;
  title: string;
  description: string;
  description_title: string;
  category: string;
  task_type: string;
  platform: string;
  reward_amount_fen: number;
  reward_amount_yuan: string;
  reward_unit: string;
  market_id?: string;
  country_code?: string;
  currency?: string;
  currency_symbol?: string;
  currency_exponent?: number;
  reward_amount_minor?: number;
  reward_display?: string;
  completed_count: number;
  max_assignments: number;
  current_assigned: number;
  assignment_timeout_seconds: number;
  require_review: boolean;
  is_recommended: boolean;
  is_new: boolean;
  icon_url: string;
  tags: string[] | null;
  rules: string;
  rules_title: string;
  review_rules: string;
  review_rules_title: string;
  steps: TaskStepDTO[] | null;
  app_info: DownloadAppInfoDTO | null;
  status: string;
  sort_order: number;
  start_at: string | null;
  end_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface TaskListResponseDTO {
  total: number;
  page: number;
  page_size: number;
  items: TaskDTO[];
}

// --- Request / Response ---

export interface CreateTaskRequest {
  title: string;
  description: string;
  description_title: string;
  category: string;
  task_type: string;
  platform: string;
  reward_amount_fen: number;
  market_id?: string;
  country_code?: string;
  currency?: string;
  currency_symbol?: string;
  currency_exponent?: number;
  reward_amount_minor?: number;
  completed_count: number;
  max_assignments: number;
  assignment_timeout_seconds: number;
  require_review: boolean;
  is_recommended: boolean;
  is_new: boolean;
  icon_url: string;
  tags: string[];
  rules: string;
  rules_title: string;
  review_rules: string;
  review_rules_title: string;
  steps: TaskStepDTO[];
  app_info?: DownloadAppInfoDTO | null;
  sort_order: number;
  start_at?: string;
  end_at?: string;
}

export interface UpdateTaskRequest {
  title: string;
  description: string;
  description_title: string;
  category: string;
  task_type: string;
  platform: string;
  reward_amount_fen: number;
  market_id?: string;
  country_code?: string;
  currency?: string;
  currency_symbol?: string;
  currency_exponent?: number;
  reward_amount_minor?: number;
  completed_count: number;
  max_assignments: number;
  assignment_timeout_seconds: number;
  require_review: boolean;
  is_recommended: boolean;
  is_new: boolean;
  icon_url: string;
  tags: string[];
  rules: string;
  rules_title: string;
  review_rules: string;
  review_rules_title: string;
  steps: TaskStepDTO[];
  app_info?: DownloadAppInfoDTO | null;
  sort_order: number;
  start_at?: string | null;
  end_at?: string | null;
}
